/**
 * Copyright © 2022-2023 Delicious AI, LLC
 */

import { DefaultContext, DocumentNode, useLazyQuery } from '@apollo/client'
import { LazyQueryHookOptions } from '@apollo/client/react/types/types'
import { useEffect, useState } from 'react'
import { useDebounce } from 'use-debounce'

export type UseDebouncedItemQueryProps<
  QueryType,
  QueryInputType extends Record<string, any>,
> = {
  queryStr: DocumentNode
  options: LazyQueryHookOptions<QueryType, QueryInputType>
  debounceDelay?: number
  queryOnMount?: boolean
  paginationPageSize?: number
  context?: DefaultContext
  initialSearchString?: string
}

export const useDebouncedItemQuery = <
  QueryType,
  QueryInputType extends Record<string, any>,
>({
  queryStr,
  options,
  debounceDelay,
  queryOnMount,
  context,
  initialSearchString = '',
}: UseDebouncedItemQueryProps<QueryType, QueryInputType>) => {
  const [itemQuery, setItemQuery] = useState(initialSearchString)
  const [debouncedQuery] = useDebounce(itemQuery, debounceDelay || 500)
  const [query, meta] = useLazyQuery<QueryType, QueryInputType>(
    queryStr,
    options,
  )

  useEffect(() => {
    if (queryOnMount && options.variables) {
      query({
        variables: options.variables,
        context,
      })
    }
  }, [])

  return {
    itemQuery,
    setItemQuery,
    debouncedQuery,
    lazyQuery: {
      query,
      meta,
    },
  }
}
